import * as React from 'react'

import {
  BoxIcon,
  Building2Icon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  InfoIcon,
  MapPinIcon,
  SettingsIcon,
  TruckIcon,
} from 'lucide-react'
import {
  action,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import ColumnsConfigurationMenu from '~/client/src/desktop/components/HeaderBar/components/ColumnsConfigurationMenu'
import ReportModal from '~/client/src/desktop/components/HeaderBar/components/ReportModal'
import BaseHeaderBar from '~/client/src/desktop/components/HeaderBar/HeaderBar'
import MaterialsFilterType from '~/client/src/desktop/enums/MaterialsFilterType'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import DynamicOverflowListStore from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList.store'
import EntityNameFilter from '~/client/src/shared/components/Filters/EntityNameFilter/EntityNameFilter'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import MaterialsGroupingOption, {
  getMaterialsGroupingOptionTranslatorKey,
  materialsGroupingOptionList,
} from '~/client/src/shared/enums/MaterialsGroupingOption'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import AreasStore from '~/client/src/shared/stores/domain/Areas.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LevelsStore from '~/client/src/shared/stores/domain/Levels.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import LogisticsObjectsStore from '~/client/src/shared/stores/domain/LogisticsObjects.store'
import MaterialCategoryStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialConfigurationStore from '~/client/src/shared/stores/domain/MaterialConfiguration.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import VerticalObjectsStore from '~/client/src/shared/stores/domain/VerticalObjects.store'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import { MaterialsDataKeys } from '../MaterialsList/MaterialsDataKeys'
import MaterialsListStore from '../MaterialsList/MaterialsList.store'
import MaterialReportButton from './components/MaterialReportButton/MaterialReportButton'
import MaterialsFilter from './components/MaterialsFilter/MaterialsFilter'
import MaterialsFilterStore from './components/MaterialsFilter/MaterialsFilter.store'
import MaterialsGroupingOptions from './components/MaterialsGroupingOptions/MaterialsGroupingOptions'
import MaterialsGroupingOptionsStore from './components/MaterialsGroupingOptions/MaterialsGroupingOptions.store'
import MaterialsHeaderBarStore from './MaterialsHeaderBar.store'

interface IProps {
  state?: DesktopInitialState
  eventsStore?: DesktopEventStore
  materialsListStore: MaterialsListStore
  materialsStore?: MaterialsStore
  materialCategoryStore?: MaterialCategoryStore
  locationAttributesStore?: LocationAttributesStore
  levelsStore?: LevelsStore
  areasStore?: AreasStore
  logisticsObjectsStore?: LogisticsObjectsStore
  verticalObjectsStore?: VerticalObjectsStore
  companiesStore?: CompaniesStore
  tagsStore?: TagsStore
  materialConfigurationStore?: MaterialConfigurationStore
}

const ICON_SIZE = 16

@inject(
  // eslint-disable-next-line
  'state',
  'eventsStore',
  'materialsStore',
  'materialCategoryStore',
  'locationAttributesStore',
  'levelsStore',
  'areasStore',
  'logisticsObjectsStore',
  'verticalObjectsStore',
  'companiesStore',
  'tagsStore',
  'materialConfigurationStore',
)
@observer
export default class MaterialsHeaderBar extends BaseHeaderBar<IProps> {
  protected store: MaterialsHeaderBarStore = null
  private readonly groupingOptionsStore: MaterialsGroupingOptionsStore = null
  private readonly filterStore: MaterialsFilterStore = null
  private readonly dynamicOverflowListStore: DynamicOverflowListStore = null

  private disposeConfigUpdateReaction: IReactionDisposer
  @observable private isMenuShown: boolean = false
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    this.dynamicOverflowListStore = new DynamicOverflowListStore()

    this.store = new MaterialsHeaderBarStore(props.eventsStore)

    const { eventsStore } = props

    const { onShowGroupByChanged, onShowChanged } = this.store

    this.filterStore = new MaterialsFilterStore(
      props.eventsStore,
      props.materialsStore,
      props.materialsListStore,
      onShowChanged,
      props.locationAttributesStore,
      props.levelsStore,
      props.areasStore,
      props.logisticsObjectsStore,
      props.verticalObjectsStore,
      props.companiesStore,
      props.tagsStore,
      props.materialCategoryStore,
      this.dynamicOverflowListStore.enableRecalculation,
    )

    this.groupingOptionsStore = new MaterialsGroupingOptionsStore(
      eventsStore,
      onShowGroupByChanged,
      this.onGroupingChanged,
      eventsStore.appState.materialFilters,
      materialsGroupingOptionList,
      MaterialsGroupingOption.NONE,
      eventsStore.appState.materialFiltersSettings.desktopFilterMap,
      getMaterialsGroupingOptionTranslatorKey,
      props.materialConfigurationStore,
    )
    makeObservable(this)
  }

  public componentDidMount() {
    this.store.loadReportTemplate()

    this.disposeConfigUpdateReaction = reaction(
      () => this.localProps.materialConfigurationStore.configuration,
      this.groupingOptionsStore.resetGroupingIfNeed,
      { fireImmediately: false },
    )
  }

  public componentWillUnmount() {
    this.disposeConfigUpdateReaction?.()
  }

  protected renderLeftSection() {
    const { forceSearchClose, onShowSearchChanged } = this.store

    return (
      <div className="row x-start full-height left-bar no-grow tw:mr-2 tw:gap-2">
        <EntityNameFilter
          filters={this.localProps.state.materialFilters}
          forceClose={forceSearchClose}
          onShowChanged={onShowSearchChanged}
        />
        {this.renderGroupingOptions()}
      </div>
    )
  }

  protected renderCenterSection() {
    const { eventsStore } = this.localProps
    return (
      <MaterialsFilter
        store={this.filterStore}
        forceCloseMap={this.store.forceCloseMap}
        eventsStore={eventsStore}
        filters={eventsStore.appState.materialFilters}
        dynamicOverflowListStore={this.dynamicOverflowListStore}
      />
    )
  }

  protected renderRightSection() {
    const { filterStoresByTypeMap } = this.filterStore
    return (
      <MaterialReportButton
        title={Localization.translator.createReport}
        materialsListStore={this.localProps.materialsListStore}
        className="ml10"
        toggleModal={this.toggleModal}
        shouldPreventCreation={true}
        filterStoresByTypeMap={filterStoresByTypeMap}
      />
    )
  }

  @action.bound
  private renderReportButton() {
    const { filterStoresByTypeMap } = this.filterStore
    const { additionalReportFiles, isAnyFileTypeChecked } = this.store
    return (
      <MaterialReportButton
        title={Localization.translator.createReport}
        materialsListStore={this.localProps.materialsListStore}
        className="ml10"
        toggleModal={this.toggleModal}
        shouldHideIcon={true}
        shouldInvertColors={true}
        filterStoresByTypeMap={filterStoresByTypeMap}
        additionalReportFiles={additionalReportFiles}
        isEnabled={isAnyFileTypeChecked}
      />
    )
  }

  @action.bound
  protected renderModal() {
    const { filterStoresByTypeMap } = this.filterStore
    const { additionalReportFiles, toggleAdditionalReportFiles } = this.store
    const now = new Date()

    return (
      <ReportModal
        startDate={now}
        endDate={now}
        toggleModal={this.toggleModal}
        getIconByFilter={this.getIconByFilter}
        filterStoresByTypeMap={filterStoresByTypeMap}
        renderButton={this.renderReportButton}
        reportName={Localization.translator.materialReport}
        additionalReportFiles={additionalReportFiles}
        toggleAdditionalReportFiles={toggleAdditionalReportFiles}
        groupedBy={this.groupingOptionsStore.groupByCaption}
        isFullReport
      />
    )
  }

  private getIconByFilter(filterType: string) {
    switch (filterType) {
      case MaterialsFilterType.STATUS:
        return <InfoIcon size={16} className="no-grow row mr10" />
      case MaterialsFilterType.MATERIAL:
        return <BoxIcon size={16} className="no-grow row mr10" />
      case MaterialsFilterType.COMPANY:
        return <Building2Icon size={16} className="no-grow row mr10" />
      case MaterialsFilterType.PLANNED_INSTALL_LOCATION:
      case MaterialsFilterType.PLANNED_DELIVERY_LOCATION:
      case MaterialsFilterType.CURRENT_LOCATION:
        return <MapPinIcon size={16} className="no-grow row mr10" />
    }
  }

  @action.bound
  private toggleMenu() {
    this.isMenuShown = !this.isMenuShown
  }

  protected renderActionBar() {
    const {
      displayedCount,
      isDataPrepared,
      isSomeRowSelected,
      selectedInstancesCount,
      canAddMaterialsToDelivery,
      isNewDeliveryBtnHidden,
      openNewFormAndInitIfNeed,
      collapseAllCategories,
      resetCollapsing,
    } = this.localProps.materialsListStore

    if (!isDataPrepared) {
      return null
    }

    return (
      <div className="row pb10 px24 bb-light-grey">
        <div className="row text large light">
          <div className="pr10 no-grow nowrap">
            {isSomeRowSelected
              ? Localization.translator.xOfYSelected(
                  selectedInstancesCount,
                  displayedCount,
                )
              : Localization.translator.xMaterials(displayedCount)}
          </div>
          <div className="row bl-light-cool-grey pl10">
            {this.renderCollapseBtn(collapseAllCategories)}
            {this.renderCollapseBtn(resetCollapsing, true)}
            {this.columnsConfiguration}
            {!isNewDeliveryBtnHidden && (
              <Button
                className="tw:ml-2 tw:justify-end tw:select-none"
                variant="outline"
                disabled={!canAddMaterialsToDelivery}
                onClick={openNewFormAndInitIfNeed}
              >
                <TruckIcon data-icon="inline-start" />
                {Localization.translator.newDelivery}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  private get columnsConfiguration(): JSX.Element {
    if (!this.localProps.state.userActiveProjectSettings.isAdmin) {
      return null
    }

    const { searchKey, changeSearchKey } = this.store
    const { columns, updateColumnsConfiguration, resetColumnsConfiguration } =
      this.localProps.materialsListStore

    return (
      <div>
        <div className="row x-end">
          <div
            className={classList({
              'text grey large pointer no-grow icon-wrap': true,
              'blue unclickable-element': this.isMenuShown,
            })}
            onClick={this.toggleMenu}
            title={Localization.translator.configureTableColumns}
          >
            <span
              className={classList({
                'no-grow pa10 br-rounded highlighted-hover tw:inline-flex': true,
                'bg-blue-light': this.isMenuShown,
              })}
            >
              <SettingsIcon size={ICON_SIZE} />
            </span>
          </div>
        </div>
        {this.isMenuShown && (
          <MenuCloser closeMenu={this.toggleMenu} className="row x-end mr40">
            <ColumnsConfigurationMenu
              columns={columns}
              restoreDefaults={resetColumnsConfiguration}
              searchKey={searchKey}
              changeSearchKey={changeSearchKey}
              updateColumnsConfiguration={updateColumnsConfiguration}
              excludedFromSort={this.excludedFromSortKeys}
            />
          </MenuCloser>
        )}
      </div>
    )
  }

  private get excludedFromSortKeys(): string[] {
    return [MaterialsDataKeys.CHECKBOX, MaterialsDataKeys.PROCUREMENT_ID]
  }

  private renderGroupingOptions() {
    return (
      <MaterialsGroupingOptions
        store={this.groupingOptionsStore}
        forceClose={this.store.forceGroupByClose}
      />
    )
  }

  private renderCollapseBtn(
    onClick: () => void,
    isExpand?: boolean,
  ): JSX.Element {
    return (
      <Button className="tw:mx-1" variant="outline" onClick={onClick}>
        <span className="pl4 tw:inline-flex" data-icon="inline-start">
          {isExpand ? <ChevronsUpDownIcon /> : <ChevronsDownUpIcon />}
        </span>
        {EMPTY_STRING}
      </Button>
    )
  }

  private onGroupingChanged = () => {
    this.dynamicOverflowListStore.enableRecalculation()
    this.localProps.materialsListStore.resetCollapsing()
  }
}
